import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { storefrontApiRequest, STOREFRONT_PRODUCTS_QUERY } from '@/lib/shopify/api';
import { formatPrice } from '@/lib/formatPrice';
import type { ShopifyProduct } from '@/lib/shopify/api';

interface Props {
  className?: string;
  onNavigate?: () => void;
}

export const LiveSearchDropdown: React.FC<Props> = ({ className, onNavigate }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ShopifyProduct[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (query.trim().length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }
    setIsSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const data = await storefrontApiRequest(STOREFRONT_PRODUCTS_QUERY, {
          first: 5,
          query: query.trim(),
        });
        const products: ShopifyProduct[] = data?.data?.products?.edges || [];
        setResults(products);
        setIsOpen(products.length > 0);
      } catch {
        setResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setIsOpen(false);
      onNavigate?.();
      window.location.href = `/catalog?q=${encodeURIComponent(query)}`;
    }
  };

  return (
    <div ref={containerRef} className={`relative ${className || ''}`}>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Input
            type="search"
            placeholder="Search products..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setIsOpen(true)}
            className="pr-10"
          />
          <Button type="submit" variant="ghost" size="icon" className="absolute right-0 top-0 h-full" aria-label="Search">
            <Search className="h-4 w-4" />
          </Button>
        </div>
      </form>

      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-card border rounded-lg shadow-xl z-50 overflow-hidden">
          {results.map((p) => {
            const img = p.node.images.edges[0]?.node;
            const price = p.node.priceRange.minVariantPrice;
            return (
              <Link
                key={p.node.id}
                to={`/product/${p.node.handle}`}
                className="flex items-center gap-3 px-3 py-2.5 hover:bg-muted transition-colors"
                onClick={() => { setIsOpen(false); setQuery(''); onNavigate?.(); }}
              >
                {img ? (
                  <img src={`${img.url.split('?')[0]}?width=80&format=webp`} alt={img.altText || p.node.title} className="w-10 h-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded bg-muted flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{p.node.title}</p>
                  <p className="text-xs font-semibold text-primary">{formatPrice(price.amount)}</p>
                </div>
              </Link>
            );
          })}
          <Link
            to={`/catalog?q=${encodeURIComponent(query)}`}
            className="block text-center text-sm text-primary font-medium py-2.5 border-t hover:bg-muted transition-colors"
            onClick={() => { setIsOpen(false); onNavigate?.(); }}
          >
            View all results →
          </Link>
        </div>
      )}
    </div>
  );
};